/**
 * Wrapper main-thread do fallback WASM (ffmpeg.wasm) do recurso m4a-recording.
 * Sobe o worker `ffmpegM4a.worker.ts`, envia o Blob gravado nativamente, repassa
 * progresso e cancelamento, e resolve com o Blob `audio/mp4` (AAC-LC).
 *
 * O worker é ÚNICO e fica vivo entre usos: o core do ffmpeg tem alguns MB e
 * carregá-lo só depois que o usuário solta o botão de gravar deixava uma espera
 * visível. `warmUpM4aWorker()` é chamado quando o gravador abre (ver
 * `AudioRecorder.vue`), então o core normalmente já está pronto na hora do
 * transcode. Só existe uma gravação por vez, então um worker basta.
 */

export interface TranscodeOptions {
    signal?: AbortSignal;
    onProgress?: (fraction: number) => void;
    /** Duração gravada (s). O WebM do MediaRecorder não tem header de duração, então
     *  o progresso do ffmpeg fica inútil; passando isso, o worker calcula por `time`. */
    durationSec?: number;
}

type WorkerMessage =
    | { type: 'progress'; value: number }
    | { type: 'done'; blob: Blob }
    | { type: 'cancelled'; message: string }
    | { type: 'warm' }
    | { type: 'error'; message: string };

/** Extensão de input para o ffmpeg a partir do mimetype do Blob nativo. */
function extFromMime(type: string): string {
    if (type.includes('webm')) return 'webm';
    if (type.includes('ogg')) return 'ogg';
    if (type.includes('mp4') || type.includes('m4a')) return 'm4a';
    if (type.includes('mpeg')) return 'mp3';
    if (type.includes('wav')) return 'wav';
    return 'webm';
}

const LOG = '[ffmpegM4a]';

/**
 * Logs de diagnóstico (default OFF). Ligar por build (`VITE_FFMPEG_DEBUG=true`) ou
 * em runtime sem rebuild (`localStorage['whiz.ffmpegDebug']='true'`). A flag é
 * propagada ao worker na mensagem. console.error é sempre emitido.
 */
const DEBUG = (() => {
    const env = import.meta.env as Record<string, string | undefined>;
    if (env.VITE_FFMPEG_DEBUG === 'true') return true;
    try {
        return typeof localStorage !== 'undefined' && localStorage.getItem('whiz.ffmpegDebug') === 'true';
    } catch {
        return false;
    }
})();
const dlog = (...args: unknown[]) => { if (DEBUG) console.log(...args); };

/** Base dos assets do core. Lida AQUI (main thread), nunca dentro do worker:
 *  `import.meta.env` em worker sai do build com `__VITE_IS_LEGACY__` solto. */
const FFMPEG_BASE =
    (import.meta.env as Record<string, string | undefined>).VITE_FFMPEG_BASE || '/ffmpeg';

let sharedWorker: Worker | null = null;

function getWorker(): Worker {
    if (sharedWorker) return sharedWorker;
    dlog(`${LOG} spawning worker`);
    sharedWorker = new Worker(new URL('./ffmpegM4a.worker.ts', import.meta.url), { type: 'module' });
    return sharedWorker;
}

/**
 * Começa a baixar/instanciar o core do ffmpeg AGORA, sem áudio nenhum. Chamado
 * quando o gravador abre no caminho `wasm`. Falha é silenciosa de propósito: o
 * transcode real tenta de novo e é ele que reporta ao usuário.
 */
export function warmUpM4aWorker(): void {
    try {
        getWorker().postMessage({ type: 'warmup', debug: DEBUG, base: FFMPEG_BASE });
        dlog(`${LOG} warmup solicitado`);
    } catch (err) {
        dlog(`${LOG} warmup não pôde ser solicitado:`, err);
    }
}

/** Derruba o worker (e o core carregado). Usado ao desmontar o gravador. */
export function disposeM4aWorker(): void {
    if (!sharedWorker) return;
    sharedWorker.terminate();
    sharedWorker = null;
}

export function transcodeToM4aViaWorker(blob: Blob, opts: TranscodeOptions = {}): Promise<Blob> {
    const { signal, onProgress, durationSec } = opts;

    return new Promise<Blob>((resolve, reject) => {
        const inputExt = extFromMime(blob.type);
        dlog(`${LOG} transcode; blob=${blob.size}B type="${blob.type}" -> ext="${inputExt}"`);
        const worker = getWorker();

        const cleanup = () => {
            // O worker CONTINUA vivo: o core já carregado é o que torna a próxima
            // gravação instantânea. Só os handlers desta chamada são soltos.
            worker.onmessage = null;
            worker.onerror = null;
            signal?.removeEventListener('abort', onAbort);
        };
        const onAbort = () => {
            dlog(`${LOG} abort signal -> posting cancel`);
            worker.postMessage({ type: 'cancel' });
        };

        if (signal) {
            if (signal.aborted) onAbort();
            signal.addEventListener('abort', onAbort);
        }

        worker.onmessage = (e: MessageEvent<WorkerMessage>) => {
            const msg = e.data;
            if (msg.type === 'progress') {
                onProgress?.(msg.value);
            } else if (msg.type === 'warm') {
                // Ack de uma pré-carga que terminou depois do início do transcode.
                dlog(`${LOG} core pré-carregado`);
            } else if (msg.type === 'done') {
                dlog(`${LOG} done: ${msg.blob.size}B ${msg.blob.type}`);
                cleanup();
                resolve(msg.blob);
            } else if (msg.type === 'cancelled') {
                dlog(`${LOG} cancelled`);
                cleanup();
                reject(new DOMException(msg.message, 'AbortError'));
            } else {
                console.error(`${LOG} worker error message:`, msg.message);
                cleanup();
                reject(new Error(msg.message));
            }
        };
        worker.onerror = (e) => {
            console.error(`${LOG} worker.onerror:`, e.message, e);
            cleanup();
            // Worker quebrado não se recupera: derruba para a próxima tentativa
            // subir um novo em vez de falar com um cadáver.
            disposeM4aWorker();
            reject(new Error(e.message || 'Worker de conversão de áudio falhou.'));
        };

        dlog(`${LOG} posting transcode message`);
        worker.postMessage({ type: 'transcode', blob, inputExt, debug: DEBUG, durationSec, base: FFMPEG_BASE });
    });
}
